/**
 * Rax Persona Editor
 *
 * Create and edit personas for multi-agent teams.
 * Configure appearance, personality, skills, and preferences.
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import {
  raxApi,
  type Persona,
  type PersonaRole,
  type PersonaAppearance,
  type PersonaCreateParams,
} from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";

const ROLE_OPTIONS: Array<{ value: PersonaRole; label: string; icon: string; desc: string }> = [
  { value: "engineer", label: "Engineer", icon: "👩‍💻", desc: "Code, debug, ship" },
  { value: "pm", label: "PM", icon: "📋", desc: "Plan, coordinate, track" },
  { value: "devops", label: "DevOps", icon: "🔧", desc: "Deploy, monitor, fix" },
  { value: "analyst", label: "Analyst", icon: "📊", desc: "Research, analyze, report" },
  { value: "writer", label: "Writer", icon: "✍️", desc: "Draft, edit, publish" },
  { value: "support", label: "Support", icon: "🎧", desc: "Help, triage, resolve" },
  { value: "custom", label: "Custom", icon: "⚡", desc: "Define your own" },
];

const HAIR_COLORS = [
  "#553322",
  "#222222",
  "#FFD700",
  "#AA4422",
  "#111111",
  "#4488CC",
  "#AA55CC",
  "#FFFFFF",
];
const HAIR_STYLES: Array<{ value: PersonaAppearance["hairStyle"]; label: string }> = [
  { value: "short", label: "Short" },
  { value: "long", label: "Long" },
  { value: "ponytail", label: "Ponytail" },
  { value: "bald", label: "Bald" },
];
const OUTFITS: Array<{ value: PersonaAppearance["outfit"]; label: string; icon: string }> = [
  { value: "hoodie", label: "Hoodie", icon: "🧥" },
  { value: "casual", label: "Casual", icon: "👕" },
  { value: "suit", label: "Suit", icon: "👔" },
  { value: "uniform", label: "Uniform", icon: "👷" },
];

const AVAILABLE_SKILLS = [
  { id: "code", name: "Code", icon: "💻" },
  { id: "terminal", name: "Terminal", icon: "⌨️" },
  { id: "github", name: "GitHub", icon: "🐙" },
  { id: "browser", name: "Browser", icon: "🌐" },
  { id: "gmail", name: "Gmail", icon: "📧" },
  { id: "calendar", name: "Calendar", icon: "📅" },
  { id: "slack", name: "Slack", icon: "💬" },
  { id: "notion", name: "Notion", icon: "📝" },
  { id: "web_search", name: "Web Search", icon: "🔍" },
  { id: "memory", name: "Memory", icon: "🧠" },
  { id: "files", name: "Files", icon: "📁" },
];

@customElement("rax-persona-editor")
export class RaxPersonaEditor extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.7);
        backdrop-filter: blur(4px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        animation: fadeIn 0.2s ease-out;
      }
      
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      
      .panel {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-xl);
        width: 100%;
        max-width: 560px;
        max-height: 90vh;
        overflow-y: auto;
        animation: slideUp 0.3s ease-out;
      }
      
      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-4);
        border-bottom: 1px solid var(--border-color);
        position: sticky;
        top: 0;
        background: var(--bg-secondary);
        z-index: 1;
      }
      
      .title {
        font-size: var(--font-xl);
        font-weight: 600;
        color: var(--text-primary);
      }
      
      .header-actions {
        display: flex;
        gap: var(--space-2);
      }
      
      .close-btn {
        background: transparent;
        border: none;
        color: var(--text-tertiary);
        font-size: 1.5rem;
        cursor: pointer;
        padding: var(--space-1);
      }
      
      .save-btn {
        background: var(--rax-yellow);
        border: none;
        border-radius: var(--radius-md);
        padding: var(--space-2) var(--space-4);
        font-weight: 600;
        color: #000;
        cursor: pointer;
      }
      
      .save-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .body {
        padding: var(--space-5);
      }
      
      .section {
        margin-bottom: var(--space-6);
      }
      
      .section-title {
        font-size: var(--font-sm);
        font-weight: 600;
        color: var(--text-secondary);
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: var(--space-3);
      }
      
      .form-row {
        margin-bottom: var(--space-4);
      }
      
      .form-label {
        display: block;
        font-size: var(--font-sm);
        font-weight: 500;
        color: var(--text-secondary);
        margin-bottom: var(--space-2);
      }
      
      .form-input {
        width: 100%;
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-3);
        font-size: var(--font-md);
        color: var(--text-primary);
      }
      
      .form-input:focus {
        outline: none;
        border-color: var(--rax-yellow);
      }
      
      .form-textarea {
        min-height: 80px;
        resize: vertical;
        font-family: inherit;
      }
      
      /* Role selector */
      .role-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: var(--space-2);
      }
      
      .role-card {
        background: var(--bg-elevated);
        border: 2px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-3);
        cursor: pointer;
        text-align: center;
        transition: all var(--transition-fast);
      }
      
      .role-card:hover {
        border-color: var(--text-tertiary);
      }
      
      .role-card.selected {
        border-color: var(--rax-yellow);
        background: rgba(255, 208, 0, 0.1);
      }
      
      .role-icon {
        font-size: 1.5rem;
        margin-bottom: var(--space-1);
      }
      
      .role-label {
        font-size: var(--font-sm);
        font-weight: 600;
        color: var(--text-primary);
      }
      
      /* Appearance */
      .appearance-preview {
        display: flex;
        align-items: center;
        gap: var(--space-4);
        padding: var(--space-4);
        background: var(--bg-elevated);
        border-radius: var(--radius-lg);
        margin-bottom: var(--space-4);
      }
      
      .avatar-preview {
        width: 64px;
        height: 64px;
        border-radius: var(--radius-md);
        background: var(--bg-subtle);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 2rem;
      }
      
      .preview-name {
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
      }
      
      .preview-role {
        font-size: var(--font-sm);
        color: var(--text-tertiary);
      }
      
      .color-picker {
        display: flex;
        gap: var(--space-2);
        flex-wrap: wrap;
      }
      
      .color-swatch {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: 2px solid transparent;
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .color-swatch:hover {
        transform: scale(1.1);
      }
      
      .color-swatch.selected {
        border-color: var(--rax-yellow);
        box-shadow:
          0 0 0 2px var(--bg-secondary),
          0 0 0 4px var(--rax-yellow);
      }
      
      .option-row {
        display: flex;
        gap: var(--space-2);
      }
      
      .option-btn {
        flex: 1;
        background: var(--bg-elevated);
        border: 2px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-2) var(--space-3);
        font-size: var(--font-sm);
        color: var(--text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .option-btn:hover {
        border-color: var(--text-tertiary);
      }
      
      .option-btn.selected {
        border-color: var(--rax-yellow);
        background: rgba(255, 208, 0, 0.1);
        color: var(--text-primary);
      }
      
      /* Skills */
      .skills-grid {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-2);
      }
      
      .skill-chip {
        display: flex;
        align-items: center;
        gap: var(--space-1);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-full);
        padding: var(--space-1) var(--space-3);
        font-size: var(--font-sm);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .skill-chip:hover {
        border-color: var(--text-tertiary);
      }
      
      .skill-chip.enabled {
        background: rgba(16, 185, 129, 0.2);
        border-color: #10b981;
        color: #10b981;
      }
      
      /* Autonomy slider */
      .autonomy-options {
        display: flex;
        gap: var(--space-2);
      }
      
      .autonomy-btn {
        flex: 1;
        background: var(--bg-elevated);
        border: 2px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-3);
        cursor: pointer;
        text-align: center;
        transition: all var(--transition-fast);
      }
      
      .autonomy-btn:hover {
        border-color: var(--text-tertiary);
      }
      
      .autonomy-btn.selected {
        border-color: var(--rax-yellow);
        background: rgba(255, 208, 0, 0.1);
      }
      
      .autonomy-label {
        font-size: var(--font-md);
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: var(--space-1);
      }
      
      .autonomy-desc {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }
      
      @media (max-width: 560px) {
        .panel {
          max-width: 100%;
          height: 100%;
          max-height: 100%;
          border-radius: 0;
        }
      
        .role-grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }
    `,
  ];

  @property({ type: Object })
  persona: Persona | null = null;

  @property({ type: Boolean })
  open = false;

  @state() private name = "";
  @state() private role: PersonaRole = "engineer";
  @state() private hairColor = HAIR_COLORS[0];
  @state() private hairStyle: PersonaAppearance["hairStyle"] = "short";
  @state() private outfit: PersonaAppearance["outfit"] = "hoodie";
  @state() private skinTone = 0;
  @state() private personalityStyle = "";
  @state() private personalityVoice = "";
  @state() private enabledSkills: string[] = ["code", "memory"];
  @state() private autonomy: "low" | "medium" | "high" = "medium";
  @state() private systemPrompt = "";
  @state() private saving = false;

  override updated(changed: Map<string, unknown>) {
    if (changed.has("persona") && this.persona) {
      this.initFromPersona(this.persona);
    }
    if (changed.has("open") && this.open && !this.persona) {
      this.resetForm();
    }
  }

  private initFromPersona(p: Persona) {
    this.name = p.name;
    this.role = p.role;
    this.hairColor = p.appearance.hairColor;
    this.hairStyle = p.appearance.hairStyle;
    this.outfit = p.appearance.outfit;
    this.skinTone = p.appearance.skinTone;
    this.personalityStyle = p.personality.style;
    this.personalityVoice = p.personality.voice;
    this.enabledSkills = [...p.skills.enabled];
    this.autonomy = p.preferences.autonomy;
    this.systemPrompt = p.systemPromptAdditions ?? "";
  }

  private resetForm() {
    this.name = "";
    this.role = "engineer";
    this.hairColor = HAIR_COLORS[0];
    this.hairStyle = "short";
    this.outfit = "hoodie";
    this.skinTone = 0;
    this.personalityStyle = "";
    this.personalityVoice = "";
    this.enabledSkills = ["code", "memory"];
    this.autonomy = "medium";
    this.systemPrompt = "";
  }

  private toggleSkill(skillId: string) {
    if (this.enabledSkills.includes(skillId)) {
      this.enabledSkills = this.enabledSkills.filter((s) => s !== skillId);
    } else {
      this.enabledSkills = [...this.enabledSkills, skillId];
    }
  }

  private get canSave(): boolean {
    return this.name.trim().length > 0;
  }

  private getRoleIcon(): string {
    return ROLE_OPTIONS.find((r) => r.value === this.role)?.icon ?? "⚡";
  }

  private async handleSave() {
    if (!this.canSave) return;

    this.saving = true;

    const params: PersonaCreateParams = {
      name: this.name,
      role: this.role,
      appearance: {
        hairColor: this.hairColor,
        hairStyle: this.hairStyle,
        outfit: this.outfit,
        skinTone: this.skinTone,
      },
      personality: {
        style: this.personalityStyle,
        voice: this.personalityVoice,
      },
      skills: {
        enabled: this.enabledSkills,
        disabled: AVAILABLE_SKILLS.filter((s) => !this.enabledSkills.includes(s.id)).map(
          (s) => s.id,
        ),
      },
      preferences: {
        autonomy: this.autonomy,
      },
      systemPromptAdditions: this.systemPrompt || undefined,
    };

    try {
      let response;
      if (this.persona) {
        response = await raxApi.updatePersona(this.persona.id, params);
      } else {
        response = await raxApi.createPersona(params);
      }

      if (response.success && response.data) {
        this.dispatchEvent(
          new CustomEvent("persona-saved", {
            detail: { persona: response.data },
            bubbles: true,
            composed: true,
          }),
        );
        this.handleClose();
      }
    } catch (err) {
      console.error("[PersonaEditor] Error saving:", err);
    } finally {
      this.saving = false;
    }
  }

  private handleClose() {
    this.dispatchEvent(new CustomEvent("close", { bubbles: true, composed: true }));
  }

  override render() {
    if (!this.open) return nothing;

    return html`
      <div class="overlay" @click=${(e: Event) => e.target === e.currentTarget && this.handleClose()}>
        <div class="panel">
          <div class="header">
            <span class="title">${this.persona ? "Edit Persona" : "New Persona"}</span>
            <div class="header-actions">
              <button class="save-btn" ?disabled=${!this.canSave || this.saving} @click=${this.handleSave}>
                ${this.saving ? "Saving..." : "Save"}
              </button>
              <button class="close-btn" @click=${this.handleClose}>&times;</button>
            </div>
          </div>

          <div class="body">
            <!-- Preview -->
            <div class="appearance-preview">
              <div class="avatar-preview" style="background: ${this.hairColor}22">
                ${this.getRoleIcon()}
              </div>
              <div>
                <div class="preview-name">${this.name || "Unnamed"}</div>
                <div class="preview-role">${ROLE_OPTIONS.find((r) => r.value === this.role)?.label}</div>
              </div>
            </div>

            <!-- Name -->
            <div class="section">
              <div class="form-row">
                <label class="form-label">Name</label>
                <input
                  class="form-input"
                  type="text"
                  placeholder="e.g., Alex, Jordan, Sam"
                  .value=${this.name}
                  @input=${(e: Event) => (this.name = (e.target as HTMLInputElement).value)}
                />
              </div>
            </div>

            <!-- Role -->
            <div class="section">
              <div class="section-title">Role</div>
              <div class="role-grid">
                ${ROLE_OPTIONS.map(
                  (r) => html`
                    <button class="role-card ${this.role === r.value ? "selected" : ""}" @click=${() => (this.role = r.value)}>
                      <div class="role-icon">${r.icon}</div>
                      <div class="role-label">${r.label}</div>
                    </button>
                  `,
                )}
              </div>
            </div>

            <!-- Appearance -->
            <div class="section">
              <div class="section-title">Appearance</div>

              <div class="form-row">
                <label class="form-label">Hair Color</label>
                <div class="color-picker">
                  ${HAIR_COLORS.map(
                    (color) => html`
                      <button
                        class="color-swatch ${this.hairColor === color ? "selected" : ""}"
                        style="background: ${color}"
                        @click=${() => (this.hairColor = color)}
                      ></button>
                    `,
                  )}
                </div>
              </div>

              <div class="form-row">
                <label class="form-label">Hair Style</label>
                <div class="option-row">
                  ${HAIR_STYLES.map(
                    (style) => html`
                      <button
                        class="option-btn ${this.hairStyle === style.value ? "selected" : ""}"
                        @click=${() => (this.hairStyle = style.value)}
                      >
                        ${style.label}
                      </button>
                    `,
                  )}
                </div>
              </div>

              <div class="form-row">
                <label class="form-label">Outfit</label>
                <div class="option-row">
                  ${OUTFITS.map(
                    (o) => html`
                      <button class="option-btn ${this.outfit === o.value ? "selected" : ""}" @click=${() => (this.outfit = o.value)}>
                        ${o.icon} ${o.label}
                      </button>
                    `,
                  )}
                </div>
              </div>
            </div>

            <!-- Personality -->
            <div class="section">
              <div class="section-title">Personality</div>

              <div class="form-row">
                <label class="form-label">Style</label>
                <input
                  class="form-input"
                  type="text"
                  placeholder="e.g., Direct, technical, ships fast"
                  .value=${this.personalityStyle}
                  @input=${(e: Event) => (this.personalityStyle = (e.target as HTMLInputElement).value)}
                />
              </div>

              <div class="form-row">
                <label class="form-label">Voice</label>
                <input
                  class="form-input"
                  type="text"
                  placeholder="e.g., Terse responses, uses code examples"
                  .value=${this.personalityVoice}
                  @input=${(e: Event) => (this.personalityVoice = (e.target as HTMLInputElement).value)}
                />
              </div>
            </div>

            <!-- Skills -->
            <div class="section">
              <div class="section-title">Skills</div>
              <div class="skills-grid">
                ${AVAILABLE_SKILLS.map(
                  (skill) => html`
                    <button
                      class="skill-chip ${this.enabledSkills.includes(skill.id) ? "enabled" : ""}"
                      @click=${() => this.toggleSkill(skill.id)}
                    >
                      <span>${skill.icon}</span>
                      <span>${skill.name}</span>
                    </button>
                  `,
                )}
              </div>
            </div>

            <!-- Autonomy -->
            <div class="section">
              <div class="section-title">Autonomy</div>
              <div class="autonomy-options">
                <button class="autonomy-btn ${this.autonomy === "low" ? "selected" : ""}" @click=${() => (this.autonomy = "low")}>
                  <div class="autonomy-label">Low</div>
                  <div class="autonomy-desc">Asks before acting</div>
                </button>
                <button class="autonomy-btn ${this.autonomy === "medium" ? "selected" : ""}" @click=${() => (this.autonomy = "medium")}>
                  <div class="autonomy-label">Medium</div>
                  <div class="autonomy-desc">Balanced</div>
                </button>
                <button class="autonomy-btn ${this.autonomy === "high" ? "selected" : ""}" @click=${() => (this.autonomy = "high")}>
                  <div class="autonomy-label">High</div>
                  <div class="autonomy-desc">Acts independently</div>
                </button>
              </div>
            </div>

            <!-- System Prompt -->
            <div class="section">
              <div class="section-title">Custom Instructions (Optional)</div>
              <textarea
                class="form-input form-textarea"
                placeholder="Additional instructions for this persona..."
                .value=${this.systemPrompt}
                @input=${(e: Event) => (this.systemPrompt = (e.target as HTMLTextAreaElement).value)}
              ></textarea>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-persona-editor": RaxPersonaEditor;
  }
}
